<template>
  <div class="demo-player" :class="{ 'demo-player--offline': !enabled }">
    <video
      v-if="enabled"
      ref="video"
      :src="load ? src : undefined"
      :poster="poster"
      preload="metadata"
      muted
      playsinline
      :loop="loop"
      :aria-label="`Демовидео: ${name}`"
      @loadedmetadata="ready"
      @timeupdate="updateTime"
      @ended="$emit('ended')"
      @error="failed = load"
      @playing="failed = false; blocked = false"
    />
    <div v-if="!enabled || failed" class="demo-video-message" role="status">
      <DemoIcon name="camera" /><span>{{
        failed ? 'Видео не удалось загрузить' : 'Трансляция отключена'
      }}</span
      ><button v-if="failed" type="button" @click.stop="retry">Повторить загрузку</button>
    </div>
    <div v-if="enabled && load && !failed && (blocked || !playing)" class="demo-video-message">
      <button type="button" @click.stop="startPlayback">Смотреть видео</button>
    </div>
    <span v-if="enabled && !failed" class="demo-video-time">{{ timestamp }}</span>
  </div>
</template>
<script setup lang="ts">
const props = defineProps<{
  src: string;
  poster: string;
  name: string;
  enabled: boolean;
  load: boolean;
  playing: boolean;
  loop: boolean;
  speed: number;
  time: number;
  seekVersion: number;
  timestamp: string;
}>();
const emit = defineEmits<{ time: [number]; ended: []; resume: []; notice: [string] }>();
const video = ref<HTMLVideoElement | null>(null);
const failed = ref(false);
const blocked = ref(false);
let disposed = false;
function align(force = false) {
  const el = video.value;
  if (!el || !Number.isFinite(el.duration)) return;
  const target = Math.min(Math.max(0, props.time), el.duration);
  if (force || Math.abs(el.currentTime - target) > 0.8) el.currentTime = target;
}
async function syncPlayback() {
  const el = video.value;
  if (!el) return;
  el.muted = true;
  el.defaultMuted = true;
  el.playsInline = true;
  el.playbackRate = props.speed;
  if (props.playing && props.enabled && props.load && !failed.value) {
    try {
      await el.play();
      if (disposed || !props.playing) el.pause();
    } catch (error) {
      if (!disposed && (error as DOMException).name === 'NotAllowedError') blocked.value = true;
    }
  } else el.pause();
}
function startPlayback() {
  blocked.value = false;
  emit('resume');
  const el = video.value;
  if (!el) return;
  el.muted = true;
  el.defaultMuted = true;
  el.playsInline = true;
  void el.play().catch((error) => {
    if (!disposed && error.name === 'NotAllowedError') blocked.value = true;
  });
}
function ready() {
  align(true);
  void syncPlayback();
}
function updateTime() {
  if (video.value && !video.value.seeking) emit('time', video.value.currentTime);
}
function retry() {
  failed.value = false;
  video.value?.load();
}
function snapshot() {
  const el = video.value;
  if (!el || el.readyState < 2) {
    emit('notice', 'Дождитесь загрузки видео, чтобы сохранить кадр.');
    return;
  }
  const canvas = document.createElement('canvas');
  canvas.width = el.videoWidth;
  canvas.height = el.videoHeight;
  canvas.getContext('2d')?.drawImage(el, 0, 0);
  canvas.toBlob(
    (blob) => {
      if (!blob || disposed) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Robotics-Vision-${props.name}-${Math.floor(el.currentTime)}s.jpg`;
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      emit('notice', 'Кадр подготовлен для скачивания.');
    },
    'image/jpeg',
    0.9,
  );
}
watch(
  () => [props.playing, props.speed, props.load, props.enabled, props.src],
  () => {
    void nextTick(syncPlayback);
  },
);
watch(
  () => props.seekVersion,
  () => align(true),
);
watch(
  () => props.time,
  () => align(),
);
onMounted(ready);
onBeforeUnmount(() => {
  disposed = true;
  video.value?.pause();
});
defineExpose({ snapshot });
</script>
